import { GoogleMap } from "@react-google-maps/api";
import { GoogleMapsOverlay } from "@deck.gl/google-maps/typed";
import { FC, memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { CSSTransition } from "react-transition-group";
import Loader from "../../../../../components/new/Loader/Loader";
import { HeatmapPoint } from "../../../../../core/models/Heatmap";
import { createHeatmapLayer, DECK_HEATMAP_CANVAS_ATTR, defaultContainerStyle, HEATMAP_BASE_RADIUS, MAP_CENTER, MAP_OPTIONS } from "./ChainMap.helpers";

type DeckOverlayInternals = { _deck?: { getCanvas?: () => HTMLCanvasElement | null } };


interface iProfileMap {
    onLoad?: () => void;
    onMapClick?: () => void;
    zoom?: number;
    isUnload?: boolean;
    mapId: string;
    isInited: boolean;
    heatmapData: HeatmapPoint[];
}

const ProfileMap: FC<iProfileMap> = ({
    onLoad = () => {},
    onMapClick,
    zoom,
    isUnload,
    mapId,
    isInited,
    heatmapData,
}) => {
    const [map, setMap] = useState<google.maps.Map | null>(null);

    const overlayRef = useRef<GoogleMapsOverlay | null>(null);
    const hasDataRef = useRef(false);
    const hasFiredLoadRef = useRef(false);
    const onLoadRef = useRef(onLoad);
    useEffect(() => { onLoadRef.current = onLoad; }, [onLoad]);

    const loaderRef = useRef<HTMLDivElement>(null);

    const mapConfig = useMemo(() => (
        {
            ...MAP_OPTIONS,
            zoom: zoom || MAP_OPTIONS.zoom
        }
    ), [zoom]);

    const handleLoadMap = useCallback((loadedMap: google.maps.Map) => {
        setMap(loadedMap);
    }, []);

    useEffect(() => {
        if (!map || !isInited) return;

        try {
            let overlay = overlayRef.current;

            if (!overlay) {
                overlay = new GoogleMapsOverlay({
                    glOptions: { preserveDrawingBuffer: true },
                    onAfterRender: () => {
                        const deckCanvas = (overlayRef.current as unknown as DeckOverlayInternals)
                            ?._deck?.getCanvas?.();
                        deckCanvas?.setAttribute(DECK_HEATMAP_CANVAS_ATTR, "true");

                        if (hasDataRef.current && !hasFiredLoadRef.current) {
                            hasFiredLoadRef.current = true;
                            onLoadRef.current();
                        }
                    },
                });
                overlay.setMap(map);
                overlayRef.current = overlay;
            }

            const containerWidth = map.getDiv?.()?.clientWidth ?? 0;
            hasDataRef.current = heatmapData.length > 0;
            overlay.setProps({
                layers: heatmapData.length
                    ? [createHeatmapLayer(heatmapData, HEATMAP_BASE_RADIUS, containerWidth)]
                    : [],
            });
        } catch (error) {
            console.error("Chain profile heatmap failed to render", error);
        }
    }, [map, isInited, heatmapData]);

    useEffect(() => () => {
        overlayRef.current?.finalize();
        overlayRef.current = null;
    }, []);

    if ( isUnload ) return null;
    return (
        <div className="ChainMap" >
            {isInited && (
                // @react-google-maps/api's GoogleMap trips a JSX-element type error under
                // this repo's mixed @types/react versions (17 vs 18). Runtime is fine.
                /* @ts-ignore */
                <GoogleMap
                    mapContainerStyle={defaultContainerStyle}
                    mapContainerClassName="ChainMap__container-map"
                    center={MAP_CENTER}
                    onLoad={handleLoadMap}
                    onClick={onMapClick}
                    options={mapConfig}
                    id={mapId}
                />
            )}

            <CSSTransition
                in={!isInited || !heatmapData.length}
                timeout={1000}
                classNames="ChainMap__loader-anim"
                nodeRef={loaderRef}
                unmountOnExit
            >
                <div ref={loaderRef}>
                    <div className="ChainMap__loader">
                        <Loader className="is-no-bg" />
                    </div>
                </div>
            </CSSTransition>
        </div>
    )
}

export default memo(ProfileMap);
